import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Image,
  TextInput,
  NumberInput,
  Textarea,
  Select,
  SimpleGrid,
  ActionIcon,
  rem,
  Center,
  Badge,
  Stack,
  LoadingOverlay,
  ColorInput,
  Alert,
  CopyButton,
  Progress,
  ScrollArea,
  Avatar,
  Accordion,
  List,
  ThemeIcon,
  Code,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  IconArrowLeft,
  IconPlus,
  IconArrowRight,
  IconTrash,
  IconPhoto,
  IconDeviceFloppy,
  IconUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconBulb,
  IconCheck,
  IconX,
  IconHelp,
  IconPackage,
  IconMouse,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";
import { useNotification } from "../adminProjectNotifications/popupnotifications"; // Check path

// --- API HELPERS ---
const BASE_URL = `${API_BASE_URL}/admin/products`;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

// ============================================================================
// 1. SHARED COMPONENTS (Loaders & Image Generators)
// ============================================================================

function DynamicLoader({ visible, progress, text }) {
  if (!visible) return null;
  return (
    <Stack
      align="center"
      justify="center"
      style={{
        flex: 1,
        border: "2px dashed var(--mantine-color-blue-4)",
        backgroundColor: "var(--mantine-color-blue-0)",
        borderRadius: 8,
        padding: "40px 20px",
      }}
    >
      <Text fw={700} c="blue.7" size="lg">
        {text}
      </Text>
      <Progress
        value={progress}
        w="80%"
        size="xl"
        radius="xl"
        striped
        animated
        color="blue"
      />
      <Text size="sm" fw={700} c="blue.7">
        {progress}%
      </Text>
    </Stack>
  );
}

function ImageLinkGenerator() {
  const [links, setLinks] = useState([]);
  const [uploadState, setUploadState] = useState({
    uploading: false,
    progress: 0,
    text: "",
  });

  const handleDrop = async (files) => {
    setUploadState({ uploading: true, progress: 0, text: "Reading files..." });
    let currentProg = 0;
    const progressInterval = setInterval(() => {
      currentProg += Math.floor(Math.random() * 10) + 5;
      if (currentProg >= 90) currentProg = 90;
      setUploadState({
        uploading: true,
        progress: currentProg,
        text: currentProg < 40 ? "Compressing images..." : "Uploading to S3...",
      });
    }, 400);

    try {
      const base64Promises = files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
              resolve({ image: reader.result, description: file.name });
            reader.onerror = reject;
          }),
      );
      const imagesPayload = await Promise.all(base64Promises);
      const res = await fetch(
        `${API_BASE_URL}/auth/bulkUploadImages`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ images: imagesPayload }),
        },
      );
      const data = await res.json();

      if (data.results) {
        clearInterval(progressInterval);
        setUploadState({ uploading: true, progress: 100, text: "Done!" });
        setTimeout(() => {
          const newLinks = data.results
            .filter((r) => r.success)
            .map((r) => r.url);
          setLinks((prev) => [...prev, ...newLinks]);
          setUploadState({ uploading: false, progress: 0, text: "" });
        }, 600);
      }
    } catch (e) {
      clearInterval(progressInterval);
      setUploadState({ uploading: false, progress: 0, text: "" });
      console.error(e);
    }
  };

  return (
    <Paper
      withBorder
      p="md"
      bg="gray.0"
      radius="md"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Text fw={600} mb="xs">
        Step 1: Generate Image Links
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        Upload images to get S3 links for your CSV.
      </Text>

      {uploadState.uploading ? (
        <DynamicLoader
          visible={true}
          progress={uploadState.progress}
          text={uploadState.text}
        />
      ) : (
        <Dropzone
          onDrop={handleDrop}
          accept={IMAGE_MIME_TYPE}
          style={{
            border: "2px dashed var(--mantine-color-gray-4)",
            padding: 20,
            textAlign: "center",
            marginBottom: links.length > 0 ? 16 : 0,
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <IconUpload size={30} color="gray" style={{ margin: "0 auto" }} />
          <Text size="sm" mt="sm">
            Drag & drop images here
          </Text>
        </Dropzone>
      )}

      {links.length > 0 && !uploadState.uploading && (
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Generated Links ({links.length}):
            </Text>
            <Button
              variant="subtle"
              color="red"
              size="compact-xs"
              onClick={() => setLinks([])}
            >
              Clear All
            </Button>
          </Group>
          <ScrollArea h={250} type="auto" offsetScrollbars pr="sm">
            <Stack gap="sm">
              {links.map((url, i) => (
                <Group key={i} gap="sm" wrap="nowrap" align="center">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ lineHeight: 0 }}
                  >
                    <Avatar
                      src={url}
                      radius="md"
                      size="md"
                      style={{ border: "1px solid #dee2e6", cursor: "pointer" }}
                    />
                  </a>
                  <TextInput
                    value={url}
                    readOnly
                    style={{ flex: 1 }}
                    size="xs"
                  />
                  <CopyButton value={url}>
                    {({ copied, copy }) => (
                      <Button
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        size="xs"
                        variant="light"
                        style={{ width: 70 }}
                      >
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              ))}
            </Stack>
          </ScrollArea>
        </Box>
      )}
    </Paper>
  );
}

export function ImageUploadComponent({ onUpload, existingUrl }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingUrl);
  useEffect(() => {
    setPreview(existingUrl);
  }, [existingUrl]);
  const handleDrop = async (files) => {
    setUploading(true);
    try {
      const base64Promises = files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
              resolve({ image: reader.result, description: file.name });
            reader.onerror = reject;
          }),
      );
      const imagesPayload = await Promise.all(base64Promises);
      const response = await fetch(
        `${API_BASE_URL}/auth/bulkUploadImages`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ images: imagesPayload }),
        },
      );
      const data = await response.json();
      if (data.results)
        data.results.forEach((res) => {
          if (res.success && res.url) onUpload(res.url);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  if (preview) {
    return (
      <Box
        pos="relative"
        w="100%"
        h="100%"
        bg="gray.1"
        style={{ borderRadius: 8, overflow: "hidden", minHeight: 120 }}
      >
        <Image src={preview} h="100%" w="100%" fit="cover" />
        <ActionIcon
          color="red"
          variant="filled"
          pos="absolute"
          bottom={8}
          right={8}
          onClick={() => {
            setPreview(""); // Clear local state
            onUpload(""); // Clear parent state
          }}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Box>
    );
  }

  // If no preview exists, show your Dropzone
  return (
    <Box h="100%" w="100%" pos="relative" style={{ minHeight: 120 }}>
      <LoadingOverlay visible={uploading} overlayProps={{ blur: 2 }} />
      <Dropzone
        onDrop={handleDrop}
        accept={IMAGE_MIME_TYPE}
        // 👇 Note: If this is used for Options, you might want to restrict this to 1 file!
        maxFiles={1}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "2px dashed var(--mantine-color-gray-4)",
          backgroundColor: "var(--mantine-color-gray-0)",
          borderRadius: 8,
          cursor: "pointer",
          padding: "10px",
        }}
      >
        <Stack align="center" gap={5} style={{ pointerEvents: "none" }}>
          <IconPhoto
            style={{
              width: rem(30),
              height: rem(30),
              color: "var(--mantine-color-dimmed)",
            }}
          />
          <Text size="xs" c="dimmed" ta="center" lh={1.2}>
            + Add Image
          </Text>
        </Stack>
      </Dropzone>
    </Box>
  );
}

// ============================================================================
// 2. BULK UPLOAD TAB
// ============================================================================

function BulkUploadTab({ onSuccess }) {
  const [uploadState, setUploadState] = useState({
    uploading: false,
    progress: 0,
    text: "",
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const { showSuccess, showError } = useNotification();

  const downloadTemplate = () => {
    const headers =
      "Handle,Title,Description,Category,ProductType,UnitPrice,TaxPercentage,ShippingFee,StockQuantity,Status,ImageURLs,VariantName,VariantPrice,VariantSize,VariantColorCode,VariantStock";

    const rows = [
      // Product 1: Graphic Tee
      "tee-cyber-01,Cyberpunk Graphic Tee,High-quality cotton tee with neon print.,Apparel,NEW_PRODUCT,1499,18,50,100,AVAILABLE,https://example.com/tee1.jpg|https://example.com/tee2.jpg,Small,1499,S,#000000,20",
      "tee-cyber-01,,,,,,,,,,Medium,1499,M,#000000,50",
      "tee-cyber-01,,,,,,,,,,Large,1499,L,#000000,30",

      // Product 2: Heavyweight Hoodie
      "hoodie-kaizen-02,Kaizen Heavyweight Hoodie,Premium embroidered Japanese streetwear.,Apparel,NEW_PRODUCT,2999,18,0,70,AVAILABLE,https://example.com/hoodie-red.jpg|https://example.com/hoodie-blk.jpg,Crimson Red L,2999,L,#DC143C,15",
      "hoodie-kaizen-02,,,,,,,,,,Crimson Red XL,2999,XL,#DC143C,25",
      "hoodie-kaizen-02,,,,,,,,,,Onyx Black L,2999,L,#000000,10",
      "hoodie-kaizen-02,,,,,,,,,,Onyx Black XL,2999,XL,#000000,20",

      // Product 3: Cyber Shades
      "shades-y2k-03,Y2K Cyber Shades,Retro-futuristic wraparound sunglasses.,Accessories,NEW_PRODUCT,899,18,40,175,AVAILABLE,https://example.com/shades-silver.jpg,Chrome Silver,899,OS,#C0C0C0,100",
      "shades-y2k-03,,,,,,,,,,Matte Black,899,OS,#000000,50",
      "shades-y2k-03,,,,,,,,,,Neon Green,999,OS,#39FF14,25",

      // Product 4: Cargo Pants
      "cargo-ota-04,Ota Life Cargo Pants,Tactical multi-pocket cargo pants.,Apparel,NEW_PRODUCT,2499,18,50,140,AVAILABLE,https://example.com/cargo-olive.jpg,Olive M,2499,M,#556B2F,40",
      "cargo-ota-04,,,,,,,,,,Olive L,2499,L,#556B2F,40",
      "cargo-ota-04,,,,,,,,,,Sand M,2499,M,#C2B280,30",
      "cargo-ota-04,,,,,,,,,,Sand L,2499,L,#C2B280,30",
    ];

    const csvContent = [headers, ...rows].join("\n");

    // --- THE FIX: USE A BLOB INSTEAD OF URI ENCODING ---
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "ota_inventory_template.csv");
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateCSVData = (data) => {
    const errors = [];
    const validStatuses = [
      "AVAILABLE",
      "OUT_OF_STOCK",
      "DISCONTINUED",
      "SUSPENDED",
      "TESTING",
      "",
    ];
    const validTypes = ["NEW_PRODUCT", "EVENT", ""];
    const seenHandles = new Set();

    data.forEach((row, index) => {
      const rowNum = index + 2;

      if (!row.Handle || row.Handle.trim() === "") {
        errors.push({
          row: rowNum,
          col: "Handle",
          issue: "Missing Handle",
          fix: "Provide a unique Handle (e.g., 'tshirt-01') to group variants.",
        });
        return;
      }

      const isNewProduct = !seenHandles.has(row.Handle);

      if (isNewProduct) {
        seenHandles.add(row.Handle);
        if (!row.Title || row.Title.trim() === "")
          errors.push({
            row: rowNum,
            col: "Title",
            issue: "Missing Title",
            fix: "Title is required for the first row of a product group.",
          });
        if (!row.UnitPrice || row.UnitPrice.trim() === "")
          errors.push({
            row: rowNum,
            col: "UnitPrice",
            issue: "Missing Base Price",
            fix: "Enter a base price for the first row.",
          });
        if (row.TaxPercentage && isNaN(parseFloat(row.TaxPercentage)))
          errors.push({
            row: rowNum,
            col: "TaxPercentage",
            issue: `Invalid Number`,
            fix: "Must be a percentage number (e.g., 18 or 5).",
          });
      }

      if (row.UnitPrice && isNaN(parseFloat(row.UnitPrice)))
        errors.push({
          row: rowNum,
          col: "UnitPrice",
          issue: `Invalid Number`,
          fix: "Remove currency symbols.",
        });
      if (row.ShippingFee && isNaN(parseFloat(row.ShippingFee)))
        errors.push({
          row: rowNum,
          col: "ShippingFee",
          issue: `Invalid Number`,
          fix: "Must be a number.",
        });
      if (row.StockQuantity && isNaN(parseFloat(row.StockQuantity)))
        errors.push({
          row: rowNum,
          col: "StockQuantity",
          issue: `Invalid Number`,
          fix: "Must be a number.",
        });
      if (row.VariantPrice && isNaN(parseFloat(row.VariantPrice)))
        errors.push({
          row: rowNum,
          col: "VariantPrice",
          issue: `Invalid Number`,
          fix: "Must be a number.",
        });
      if (row.VariantStock && isNaN(parseFloat(row.VariantStock)))
        errors.push({
          row: rowNum,
          col: "VariantStock",
          issue: `Invalid Number`,
          fix: "Must be a number.",
        });

      if (
        row.Status &&
        !validStatuses.includes(row.Status.toUpperCase().trim())
      )
        errors.push({
          row: rowNum,
          col: "Status",
          issue: `Unknown Status`,
          fix: "Use: AVAILABLE, OUT_OF_STOCK, etc.",
        });
      if (
        row.ProductType &&
        !validTypes.includes(row.ProductType.toUpperCase().trim())
      )
        errors.push({
          row: rowNum,
          col: "ProductType",
          issue: `Unknown Type`,
          fix: "Use: NEW_PRODUCT or EVENT.",
        });
      if (row.ImageURLs && row.ImageURLs.includes(","))
        errors.push({
          row: rowNum,
          col: "ImageURLs",
          issue: "Contains Commas",
          fix: "Use a pipe (|) to separate links.",
        });
    });
    return errors;
  };

  const handleCsvDrop = (files) => {
    const file = files[0];
    setValidationErrors([]);
    setUploadState({
      uploading: true,
      progress: 10,
      text: "Reading CSV file...",
    });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: async (results) => {
        setUploadState({
          uploading: true,
          progress: 20,
          text: "Validating data...",
        });
        const foundErrors = validateCSVData(results.data);

        if (foundErrors.length > 0) {
          setValidationErrors(foundErrors);
          setUploadState({ uploading: false, progress: 0, text: "" });
          return;
        }

        try {
          let currentProg = 20;
          const progressInterval = setInterval(() => {
            currentProg += Math.floor(Math.random() * 8) + 2;
            if (currentProg >= 95) currentProg = 95;
            setUploadState({
              uploading: true,
              progress: currentProg,
              text: "Processing Handle Groups...",
            });
          }, 500);

          // Map payload, including the new Handle!
          const payload = results.data.map((row) => ({
            handle: row.Handle.trim(), // <--- NEW MAPPING
            title: row.Title,
            description: row.Description,
            category: row.Category,
            productType: row.ProductType
              ? row.ProductType.toUpperCase()
              : "NEW_PRODUCT",
            unitPrice: row.UnitPrice,
            taxAmount: row.TaxPercentage ? parseFloat(row.TaxPercentage) : 18,
            shippingFee: row.ShippingFee,
            stockQuantity: row.StockQuantity,
            status: row.Status ? row.Status.toUpperCase() : "AVAILABLE",
            imageURLs: row.ImageURLs,
            variantName: row.VariantName,
            variantPrice: row.VariantPrice,
            variantSize: row.VariantSize,
            variantColorCode: row.VariantColorCode,
            variantStock: row.VariantStock,
          }));

          const res = await fetch(`${BASE_URL}/bulk`, {
            method: "POST",
            headers: getAuthHeaders(), // Or getAuthHeaders() for admin
            body: JSON.stringify({ products: payload }),
          });
          const data = await res.json();

          clearInterval(progressInterval);

          if (data.success) {
            setUploadState({
              uploading: true,
              progress: 100,
              text: "Success!",
            });
            setTimeout(() => {
              showSuccess("Upload Complete", data.message);
              onSuccess();
            }, 800);
          } else {
            setUploadState({ uploading: false, progress: 0, text: "" });
            showError(
              "Backend Error",
              data.error || "Failed to save products.",
            );
          }
        } catch (e) {
          setUploadState({ uploading: false, progress: 0, text: "" });
          showError("Error", "Network request failed.");
        }
      },
      error: (err) => {
        setUploadState({ uploading: false, progress: 0, text: "" });
        showError("File Error", err.message);
      },
    });
  };

  return (
    <Stack gap="xl">
      <Alert
        title="Bulk Product Creation"
        color="blue"
        icon={<IconFileSpreadsheet />}
      >
        Use the default CSV template to create multiple products at once. Pipe
        (|) separate image URLs.
      </Alert>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <ImageLinkGenerator />

        <Paper
          withBorder
          p="md"
          radius="md"
          style={{ display: "flex", flexDirection: "column" }}
        >
          {validationErrors.length > 0 ? (
            <Stack style={{ flex: 1 }}>
              <Group justify="space-between">
                <Text fw={700} c="red.7">
                  ⚠️ Found {validationErrors.length} Errors
                </Text>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setValidationErrors([])}
                >
                  Reset
                </Button>
              </Group>
              <ScrollArea h={320} type="auto" offsetScrollbars>
                <Stack gap="xs">
                  {validationErrors.map((err, idx) => (
                    <Alert key={idx} color="red" variant="light" p="xs">
                      <Group justify="space-between" mb={4}>
                        <Badge color="red" variant="filled" size="sm">
                          Row {err.row}
                        </Badge>
                        <Text size="xs" fw={700}>
                          Column: {err.col}
                        </Text>
                      </Group>
                      <Text size="sm" fw={600} mb={2}>
                        {err.issue}
                      </Text>
                      <Text size="xs" c="dimmed">
                        <b>Fix:</b> {err.fix}
                      </Text>
                    </Alert>
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          ) : uploadState.uploading ? (
            <DynamicLoader
              visible={true}
              progress={uploadState.progress}
              text={uploadState.text}
            />
          ) : (
            <>
              <Text fw={600} mb="xs">
                Step 2: Upload CSV
              </Text>
              <Button
                variant="light"
                fullWidth
                mb="md"
                leftSection={<IconDownload size={16} />}
                onClick={downloadTemplate}
              >
                Download Template
              </Button>
              <Box
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <Dropzone
                  onDrop={handleCsvDrop}
                  accept={["text/csv"]}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "2px dashed var(--mantine-color-blue-4)",
                    padding: 40,
                    backgroundColor: "var(--mantine-color-blue-0)",
                  }}
                >
                  <IconFileSpreadsheet
                    size={40}
                    color="var(--mantine-color-blue-6)"
                  />
                  <Text fw={600} mt="sm" c="blue.7">
                    Drop CSV here
                  </Text>
                </Dropzone>
              </Box>
            </>
          )}
        </Paper>
      </SimpleGrid>
      <Accordion variant="separated" radius="md">
        <Accordion.Item value="guide">
          <Accordion.Control
            icon={<IconBulb size={20} color="var(--mantine-color-yellow-6)" />}
          >
            <Text fw={600}>Formatting Guide & Best Practices</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mt="sm">
              {/* How Grouping Works */}
              <Stack gap="sm">
                <Text fw={700} size="sm" c="blue.7">
                  How Variants & Handles Work
                </Text>
                <Text size="sm" c="dimmed">
                  The <b>Handle</b> column groups variants together into a
                  single product. If you have a shirt with 3 sizes, create 3
                  rows with the <i>exact same Handle</i>. Put the Title,
                  Description, and Images on the first row, and just the Variant
                  details on the subsequent rows.
                </Text>
              </Stack>

              {/* Troubleshooting */}
              <Stack gap="sm">
                <Text fw={700} size="sm" c="orange.7">
                  Common Troubleshooting
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon color="orange" size={20} radius="xl">
                      <IconHelp size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>
                    <b>Invalid File Type:</b> Make sure you Save/Export your
                    file strictly as a <b>.csv</b>, not .xlsx (Excel) or
                    .numbers.
                  </List.Item>
                  <List.Item>
                    <b>Cut-off Data:</b> Avoid using quotation marks (") inside
                    your descriptions as it can confuse the CSV reader.
                  </List.Item>
                </List>
              </Stack>

              {/* DOs */}
              <Stack gap="sm">
                <Text fw={700} size="sm" c="green.7">
                  What you SHOULD do
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon color="green" size={20} radius="xl">
                      <IconCheck size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>
                    <b>Use Pipes for Images:</b> Separate multiple image links
                    using the pipe symbol ( <b>|</b> ). E.g.,{" "}
                    <Code>img1.jpg|img2.jpg</Code>
                  </List.Item>
                  <List.Item>
                    <b>Match Enums:</b> For <Code>status</Code> use exactly{" "}
                    <Code>AVAILABLE</Code>, <Code>SUSPENDED</Code>,{" "}
                    <Code>TESTING</Code>, and for <Code>ProductType</Code> use{" "}
                    <Code>NEW_PRODUCT</Code>, or <Code>EVENT</Code>.
                  </List.Item>
                  <List.Item>
                    <b>Use Hex Colors:</b> For color variants, use proper hex
                    codes like <Code>#FF0000</Code>.
                  </List.Item>
                </List>
              </Stack>

              {/* DONTs */}
              <Stack gap="sm">
                <Text fw={700} size="sm" c="red.7">
                  What you MUST AVOID
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon color="red" size={20} radius="xl">
                      <IconX size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>
                    <b>No Currency Symbols:</b> Do not type <Code>₹500</Code> or{" "}
                    <Code>$50</Code>. Just type <Code>500</Code>.
                  </List.Item>
                  <List.Item>
                    <b>No Commas in Links:</b> Commas break the CSV columns.
                    Never use them to separate images.
                  </List.Item>
                  <List.Item>
                    <b>Don't change headers:</b> Do not rename, add, or delete
                    the top header row of the template.
                  </List.Item>
                </List>
              </Stack>
            </SimpleGrid>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

// ============================================================================
// 3. SINGLE PRODUCT FORM
// ============================================================================

function AdminSingleProductForm({ onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    productType: "NEW_PRODUCT",
    unitPrice: "",
    taxPercentage: 18,
    shippingFee: 0,
    stockQuantity: 0,
    images: [],
    variants: [],
    status: "AVAILABLE",
  });

  const handleImageUpload = (url) => {
    if (url)
      setFormData((prev) => ({ ...prev, images: [...prev.images, url] }));
  };
  const removeImage = (idx) =>
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  const setAsPrimary = (idx) => {
    if (idx === 0) return;
    setFormData((prev) => {
      const newImgs = [...prev.images];
      const [sel] = newImgs.splice(idx, 1);
      newImgs.unshift(sel);
      return { ...prev, images: newImgs };
    });
  };
  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const newImgs = [...prev.images];
      const tar = idx + dir;
      if (tar < 0 || tar >= newImgs.length) return prev;
      [newImgs[idx], newImgs[tar]] = [newImgs[tar], newImgs[idx]];
      return { ...prev, images: newImgs };
    });
  };

  const addVariant = () =>
    setFormData((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          name: "",
          size: "",
          colorCode: "",
          price: prev.unitPrice || 0,
          stock: 0,
        },
      ],
    }));
  const updateVariant = (idx, f, v) => {
    const n = [...formData.variants];
    n[idx][f] = v;
    setFormData((prev) => ({ ...prev, variants: n }));
  };
  const removeVariant = (idx) =>
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async () => {
    if (!formData.title || !formData.unitPrice)
      return showError("Missing Data", "Title and Price required.");
    setLoading(true);

    const payload = {
      ...formData,
      taxAmount: formData.taxPercentage,
    };
    try {
      const res = await fetch(BASE_URL, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Created", "Product created successfully");
        onSuccess();
      } else showError("Error", data.error);
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      <Group justify="flex-end" mb="md">
        <Button
          leftSection={<IconDeviceFloppy size={18} />}
          onClick={handleSubmit}
          className="btn-ota-primary"
        >
          Publish Product
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
        <Stack className="md:col-span-2">
          {/* Basic Info */}
          <Paper p="lg" radius="md" withBorder>
            <Text fw={700} mb="md">
              Basic Information
            </Text>
            <Stack gap="sm">
              <TextInput
                label="Title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              <Textarea
                label="Description"
                minRows={4}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Stack>
          </Paper>

          {/* Images */}
          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={700}>Images</Text>
              <Badge variant="light" color="blue">
                {formData.images.length}
              </Badge>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
              {formData.images.map((url, idx) => (
                <Box
                  key={idx}
                  pos="relative"
                  h={140}
                  className="group"
                  style={{
                    borderRadius: 8,
                    overflow: "hidden",
                    border:
                      idx === 0
                        ? "2px solid var(--mantine-color-blue-6)"
                        : "1px solid #eee",
                  }}
                >
                  <img
                    src={url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      padding: 8,
                    }}
                  >
                    <Group justify="flex-end">
                      <ActionIcon
                        color="red"
                        variant="filled"
                        size="sm"
                        onClick={() => removeImage(idx)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                    {idx !== 0 && (
                      <Center>
                        <Button
                          size="xs"
                          variant="white"
                          radius="xl"
                          onClick={() => setAsPrimary(idx)}
                        >
                          Set Primary
                        </Button>
                      </Center>
                    )}
                    <Group justify="space-between">
                      <ActionIcon
                        variant="transparent"
                        c="white"
                        disabled={idx === 0}
                        onClick={() => moveImage(idx, -1)}
                      >
                        <IconArrowLeft size={18} />
                      </ActionIcon>
                      <ActionIcon
                        variant="transparent"
                        c="white"
                        disabled={idx === formData.images.length - 1}
                        onClick={() => moveImage(idx, 1)}
                      >
                        <IconArrowRight size={18} />
                      </ActionIcon>
                    </Group>
                  </Box>
                  {idx === 0 && (
                    <Box
                      pos="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      bg="blue.6"
                      c="white"
                      ta="center"
                      fz={10}
                      fw={700}
                      className="group-hover:hidden"
                    >
                      PRIMARY
                    </Box>
                  )}
                </Box>
              ))}
              <Box h={140}>
                <ImageUploadComponent onUpload={handleImageUpload} />
              </Box>
            </SimpleGrid>
          </Paper>

          {/* Variants */}
          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={700}>Variants</Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={addVariant}
              >
                Add Variant
              </Button>
            </Group>
            {formData.variants.length > 0 ? (
              <Stack>
                {formData.variants.map((v, idx) => (
                  <Paper key={idx} withBorder p="sm" bg="gray.0">
                    <Group grow align="start">
                      <TextInput
                        label="Name"
                        value={v.name}
                        onChange={(e) =>
                          updateVariant(idx, "name", e.target.value)
                        }
                      />
                      <TextInput
                        label="Size"
                        value={v.size}
                        onChange={(e) =>
                          updateVariant(idx, "size", e.target.value)
                        }
                      />
                    </Group>
                    <Group grow mt="xs">
                      <ColorInput
                        label="Color"
                        value={v.colorCode}
                        onChange={(val) => updateVariant(idx, "colorCode", val)}
                      />
                      <NumberInput
                        label="Price"
                        prefix="₹"
                        value={v.price}
                        onChange={(val) => updateVariant(idx, "price", val)}
                      />
                      <NumberInput
                        label="Stock"
                        value={v.stock}
                        onChange={(val) => updateVariant(idx, "stock", val)}
                      />
                    </Group>
                    <Group justify="flex-end" mt="xs">
                      <Button
                        color="red"
                        variant="subtle"
                        size="xs"
                        onClick={() => removeVariant(idx)}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Text c="dimmed" size="sm" ta="center">
                No variants added.
              </Text>
            )}
          </Paper>
        </Stack>

        <Stack>
          <Paper p="lg" radius="md" withBorder>
            <Text fw={700} mb="md">
              Pricing & Inventory
            </Text>
            <Stack gap="sm">
              <NumberInput
                label="Base Price"
                required
                prefix="₹"
                value={formData.unitPrice}
                onChange={(v) => setFormData({ ...formData, unitPrice: v })}
              />
              <NumberInput
                label="Tax Percentage (%)"
                required
                min={0}
                max={100}
                value={formData.taxPercentage}
                onChange={(v) => setFormData({ ...formData, taxPercentage: v })}
              />
              <NumberInput
                label="Stock"
                disabled={formData.variants.length > 0}
                value={formData.stockQuantity}
                onChange={(v) => setFormData({ ...formData, stockQuantity: v })}
              />
              <NumberInput
                label="Shipping"
                prefix="₹"
                value={formData.shippingFee}
                onChange={(v) => setFormData({ ...formData, shippingFee: v })}
              />
            </Stack>
          </Paper>
          <Paper p="lg" radius="md" withBorder>
            <Text fw={700} mb="md">
              Category & Status
            </Text>
            <Stack gap="sm">
              <TextInput
                label="Category"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
              />
              <Select
                label="Type"
                data={[
                  { value: "NEW_PRODUCT", label: "Standard" },
                  { value: "EVENT", label: "Event" },
                ]}
                value={formData.productType}
                onChange={(v) => setFormData({ ...formData, productType: v })}
              />
              <Select
                label="Status"
                data={[
                  "AVAILABLE",
                  "OUT_OF_STOCK",
                  "DISCONTINUED",
                  "SUSPENDED",
                ]}
                value={formData.status}
                onChange={(v) => setFormData({ ...formData, status: v })}
              />
            </Stack>
          </Paper>
        </Stack>
      </SimpleGrid>
    </Box>
  );
}

// ============================================================================
// MAIN WRAPPER COMPONENT
// ============================================================================

export default function CreateProductWrapper() {
  const navigate = useNavigate();
  const [creationMode, setCreationMode] = useState("select");

  const handleSuccessRedirect = () =>
    navigate("/admin/dashboard/product-management");
  const handleBack = () => {
    creationMode !== "select" ? setCreationMode("select") : navigate(-1);
  };

  return (
    <Box maxW={1200} mx="auto" pb={50}>
      <Group mb="xl">
        <ActionIcon variant="light" size="lg" onClick={handleBack}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            {creationMode === "select"
              ? "How would you like to add products?"
              : "Add Global Product"}
          </Text>
          <Badge color="gray" variant="light">
            Admin Inventory
          </Badge>
        </Box>
      </Group>

      {creationMode === "select" && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" mt="xl">
          <Paper
            withBorder
            radius="md"
            p="xl"
            className="hover:shadow-md transition cursor-pointer"
            onClick={() => setCreationMode("single")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-4px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            <ThemeIcon
              size={80}
              radius="100%"
              variant="light"
              color="blue"
              mb="md"
            >
              <IconPackage size={40} />
            </ThemeIcon>
            <Text size="xl" fw={700}>
              Manual Creation
            </Text>
            <Text c="dimmed" mt="sm" style={{ flex: 1 }}>
              Perfect for adding a single global item. Enter details, upload
              images, and configure specific variants step-by-step.
            </Text>
            <Button
              mt="xl"
              variant="light"
              w="100%"
              leftSection={<IconMouse size={18} />}
            >
              Create Single Product
            </Button>
          </Paper>

          <Paper
            withBorder
            radius="md"
            p="xl"
            className="hover:shadow-md transition cursor-pointer"
            onClick={() => setCreationMode("bulk")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-4px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            <ThemeIcon
              size={80}
              radius="100%"
              variant="light"
              color="grape"
              mb="md"
            >
              <IconFileSpreadsheet size={40} />
            </ThemeIcon>
            <Text size="xl" fw={700}>
              Bulk CSV Upload
            </Text>
            <Text c="dimmed" mt="sm" style={{ flex: 1 }}>
              Upload massive catalogs in seconds. Use our template with Handle
              grouping to automatically assign variants.
            </Text>
            <Button
              mt="xl"
              variant="light"
              color="grape"
              w="100%"
              leftSection={<IconFileSpreadsheet size={18} />}
            >
              Open Bulk Uploader
            </Button>
          </Paper>
        </SimpleGrid>
      )}

      {creationMode === "single" && (
        <Stack animation="fade">
          <AdminSingleProductForm onSuccess={handleSuccessRedirect} />
        </Stack>
      )}
      {creationMode === "bulk" && (
        <Stack animation="fade">
          <BulkUploadTab onSuccess={handleSuccessRedirect} />
        </Stack>
      )}
    </Box>
  );
}
